import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ENVIRONMENT_INITIALIZER, EnvironmentProviders, importProvidersFrom, inject, Injector, Provider } from '@angular/core';
import { MATERIAL_SANITY_CHECKS } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { DEVSTROUPE_MOCK_API_DEFAULT_DELAY, mockApiInterceptor } from '@devstroupe/lib/mock-api';
import { DevstroupeConfig } from '@devstroupe/services/config';
import { DEVSTROUPE_CONFIG } from '@devstroupe/services/config/config.constants';
import { DevstroupeConfirmationService } from '@devstroupe/services/confirmation';
import { devstroupeLoadingInterceptor, DevstroupeLoadingService } from '@devstroupe/services/loading';
import { DevstroupeMediaWatcherService } from '@devstroupe/services/media-watcher';
import { DevstroupePlatformService } from '@devstroupe/services/platform';
import { DevstroupeSplashScreenService } from '@devstroupe/services/splash-screen';
import { DevstroupeUtilsService } from '@devstroupe/services/utils';
import { DevstroupeInitAppService } from './services/init-app';
import { StorageKeysService } from 'app/shared/services/storage-keys.service';

export type DevstroupeProviderConfig = {
    mockApi?: {
        delay?: number;
        services?: any[];
    };
    devstroupe?: DevstroupeConfig;
};

/**
 * Devstroupe provider
 */
export const provideDevstroupe = (config: DevstroupeProviderConfig): Array<Provider | EnvironmentProviders> => {
    // Base providers
    const providers: Array<Provider | EnvironmentProviders> = [
        {
            // Disable 'theme' sanity check
            provide: MATERIAL_SANITY_CHECKS,
            useValue: {
                doctype: true,
                theme: false,
                version: true,
            },
        },
        {
            // Use the 'fill' appearance on Angular Material form fields by default
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                appearance: 'fill',
            },
        },
        {
            provide: DEVSTROUPE_MOCK_API_DEFAULT_DELAY,
            useValue: config?.mockApi?.delay ?? 0,
        },
        {
            provide: DEVSTROUPE_CONFIG,
            useValue: config?.devstroupe ?? {},
        },

        importProvidersFrom(MatDialogModule),
        {
            provide: ENVIRONMENT_INITIALIZER,
            useValue: () => inject(DevstroupeConfirmationService),
            multi: true,
        },

        provideHttpClient(withInterceptors([devstroupeLoadingInterceptor])),
        {
            provide: ENVIRONMENT_INITIALIZER,
            useValue: () => inject(DevstroupeLoadingService),
            multi: true,
        },

        {
            provide: ENVIRONMENT_INITIALIZER,
            useValue: () => inject(DevstroupeMediaWatcherService),
            multi: true,
        },
        {
            provide: ENVIRONMENT_INITIALIZER,
            useValue: () => inject(DevstroupePlatformService),
            multi: true,
        },
        {
            provide: ENVIRONMENT_INITIALIZER,
            useValue: () => inject(DevstroupeSplashScreenService),
            multi: true,
        },
        {
            provide: ENVIRONMENT_INITIALIZER,
            useValue: () => inject(DevstroupeUtilsService),
            multi: true,
        },
        // ======TESTANDO AINDA========
        {
            provide: APP_INITIALIZER,
            useFactory: (injector: Injector) => {
                return () => {
                    const skey = injector.get(StorageKeysService);
                    const token = localStorage.getItem(skey.token);
                    if (!token) {
                        // sem token: não faz nada, resolve imediatamente
                        return Promise.resolve();
                    }
                    // com token: obtém o service e executa init()
                    const initSvc = injector.get(DevstroupeInitAppService);
                    return initSvc.init();
                };
            },
            deps: [Injector],
            multi: true,
        },
    ];

    // Mock Api services
    if (config?.mockApi?.services) {
        providers.push(provideHttpClient(withInterceptors([mockApiInterceptor])), {
            provide: APP_INITIALIZER,
            deps: [...config.mockApi.services],
            useFactory: () => (): any => null,
            multi: true,
        });
    }

    // Return the providers
    return providers;
};
